/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ColDef,
  GridReadyEvent,
  IServerSideDatasource,
  RowDoubleClickedEvent,
  RowSelectedEvent,
  RowSelectionOptions,
} from '@ag-grid-community/core';
import { Component, HostListener, inject, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Entity } from 'breeze-client';
import EntityFinderDialogHtml from './entity-finder.dialog.html';
import { AgFns, GridOptionsExt, ISortModel, RowSelectionMultipleCheckbox, RowSelectionSingle } from '../ag-grid';
import { MultiRowSelectionOptions } from '@ag-grid-community/core/dist/types/src/entities/gridOptions';
import { ResponsiveService } from '../../services/responsive.service';

export interface EntityFinderParams {
  header?: string;
  idProp?: string;
  nameProp?: string;
  width?: string;
  height?: string;
  rowSelection?: RowSelectionOptions | 'multiple' | 'single';
  rowModelType?: 'serverSide' | 'clientSide';
  showClear?: boolean;
  modal?: boolean;
  hasColSizeButtons?: boolean;
}

export type OptionalEntityFinderParams = Partial<EntityFinderParams>;

@Component({
  selector: 'prox-entity-finder',
  template: '',
  standalone: false,
})
// export abstract class EntityFinderDialog<T extends Entity, TArgs extends EntityFinderParams> implements OnInit {
// eslint-disable-next-line @angular-eslint/component-class-suffix
export abstract class EntityFinderDialog<T, TParams extends EntityFinderParams> implements OnInit {
  public static HTML = EntityFinderDialogHtml;
  private responsiveService = inject(ResponsiveService);
  firstRender = true;
  initParams!: EntityFinderParams;
  getRowId?: any;
  hasColSizeButtons = true;

  defaultParams: EntityFinderParams = {
    header: 'Select something...',
    idProp: 'id',
    nameProp: 'name',
    width: 'calc(60vw)',
    height: 'calc(60vh)',
    rowSelection: RowSelectionSingle,
    rowModelType: 'serverSide',
    showClear: false,
    modal: true,
    hasColSizeButtons: true,
  };

  datasource!: IServerSideDatasource | T[];
  gridOptions!: GridOptionsExt;
  selectedEntity?: T = undefined;
  isPageReady = false;
  rowSelection!: RowSelectionOptions;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig<TParams>,
  ) {}

  async ngOnInit() {
    const initParams = await this.initialize();
    this.initParams = { ...this.defaultParams, ...initParams, ...this.config.data };

    // Hide column size buttons on small screens (< 1536px)
    if (this.responsiveService.lt2xl()) {
      this.initParams.hasColSizeButtons = false;
    }

    this.config.header = this.initParams.header;

    this.config.closeOnEscape = false;

    this.datasource = await this.initDatasource();
    const rowModelType = AgFns.implementsIServerSideDatasouce(this.datasource) ? 'serverSide' : 'clientSide';

    if (this.initParams.rowSelection === 'multiple') {
      this.rowSelection = RowSelectionMultipleCheckbox;
    } else if (this.initParams.rowSelection === 'single') {
      this.rowSelection = RowSelectionSingle;
    } else {
      this.rowSelection = this.initParams.rowSelection ?? RowSelectionSingle;
    }

    // We DON'T want header checkboxes on server-side row models.
    if (rowModelType === 'serverSide' && this.rowSelection.mode === 'multiRow') {
      this.rowSelection.headerCheckbox = false;
    }

    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
      onRowSelected: this.onRowSelected,
      onRowDoubleClicked: this.onRowDoubleClicked,
      rowSelection: this.rowSelection,
      rowModelType: rowModelType,
    });

    if (this.getRowId != null) this.gridOptions.getRowId = this.getRowId;

    // NOT SURE WHY THIS IS HERE
    // if (this.gridOptions.rowSelection == 'multiple') {
    //   this.gridOptions.suppressRowClickSelection = true;
    // };

    this.isPageReady = true;
  }

  async initialize(): Promise<Partial<TParams>> {
    return {} as const;
  }

  async onGridReady(event: GridReadyEvent) {
    const [colDefs, sortModel] = this.getColDefsAndSortModel();

    AgFns.initGrid(event, colDefs, sortModel);
    const gridApi = this.gridOptions.apiX;
    if (AgFns.implementsIServerSideDatasouce(this.datasource)) {
      gridApi?.setGridOption('serverSideDatasource', this.datasource);
    } else {
      gridApi?.setGridOption('rowData', this.datasource);
    }
  }

  abstract initDatasource(): Promise<IServerSideDatasource | T[]>;

  getColDefsAndSortModel() {
    const colDefs = [{ field: this.initParams.nameProp }] as ColDef[];
    const sortModel = [{ colId: this.initParams.nameProp }] as ISortModel;
    return [colDefs, sortModel] as const;
  }

  onRowSelected(event: RowSelectedEvent) {
    this.selectedEntity = <T>event.data;
    // Deliberately commented out the code below, as it was causing issues with the first row selection.
    // if (this.firstRender) {
    //   this.gridOptions.apiX?.getDisplayedRowAtIndex(0)?.setSelected(false);
    //   this.firstRender = false;
    // }
  }

  onRowDoubleClicked(event: RowDoubleClickedEvent) {
    if (!event.node.isSelected()) {
      return;
    }
    this.selectedEntity = event.data as T;
    this.ref.close([this.selectedEntity]);
  }

  onOk() {
    let rows: T[];
    if (this.rowSelection.mode == 'multiRow') {
      // Code below is handle this case from the AG Grid docs -
      // When using header checkbox selection with the server-side row model,
      // you should not use the api functions getSelectedNodes() or getSelectedRows().
      const hasHeaderCheckbox = (this.rowSelection as MultiRowSelectionOptions)?.headerCheckbox ?? false;
      if (this.gridOptions.rowModelType == 'clientSide' || !hasHeaderCheckbox) {
        rows = (this.gridOptions.apiX?.getSelectedRows() as T[]) || [];
      } else {
        rows = [];
        this.gridOptions.apiX?.forEachNode((node) => {
          if (node.isSelected()) {
            if (node.data) {
              rows.push(node.data as T);
            }
          }
        });
      }

      this.ref.close(rows);
    } else {
      this.ref.close([this.selectedEntity]);
    }
  }

  onClear() {}

  @HostListener('document:keydown.escape', ['$event'])
  onCancel() {
    this.ref.close([]);
  }
}
